import { useState } from "react"
import { X, History, Plus, Sparkles, Maximize2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAiStore } from "@/stores/ai-store"
import { useAiConversation } from "@/hooks/use-ai-conversations"
import { useAiStream } from "@/hooks/use-ai-stream"
import { useAiChat } from "@/hooks/use-ai-chat"
import { AiChatMessages } from "./ai-chat-messages"
import { AiComposer } from "./ai-composer"
import { AiSuggestedPrompts } from "./ai-suggested-prompts"
import { AiConversationList } from "./ai-conversation-list"
import type { MentionEntity } from "@/lib/ag-ui-types"

export function AiSidebar() {
  const { isOpen, close, activeConversationId, mode } = useAiStore()
  const { send, startNewChat } = useAiChat()
  const [showHistory, setShowHistory] = useState(false)
  const navigate = useNavigate()

  // Subscribe to AI streaming channel
  useAiStream()

  // Load active conversation messages
  const { data: conversationData } = useAiConversation(activeConversationId)
  const messages = conversationData?.messages || []

  const hasMessages = messages.length > 0

  // Don't show sidebar in fullpage mode
  if (!isOpen || mode === "fullpage") return null

  const handleSend = (content: string, _mentions: MentionEntity[]) => {
    send(content)
  }

  const handleOpenFullPage = () => {
    close()
    if (activeConversationId) {
      navigate(`/ai/${activeConversationId}`)
    } else {
      navigate("/ai")
    }
  }

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
        onClick={close}
      />

      {/* Sidebar panel */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full w-[420px] max-w-[100vw] flex-col border-l border-border bg-[var(--bg-surface)]"
        style={{ animation: "slideInRight 200ms ease-out" }}
      >
        {/* Header */}
        <div className="flex h-10 items-center justify-between border-b border-border px-4">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[var(--text-muted)]" />
            <span className="text-sm font-semibold text-foreground">OkeyAI</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleOpenFullPage}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              title="Otwórz na pełnej stronie"
            >
              <Maximize2 size={14} />
            </button>
            <button
              onClick={startNewChat}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              title="Nowa rozmowa"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              title="Historia rozmów"
            >
              <History size={16} />
            </button>
            <button
              onClick={close}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              title="Zamknij"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content area */}
        {showHistory ? (
          <AiConversationList onBack={() => setShowHistory(false)} />
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            {hasMessages ? (
              <AiChatMessages messages={messages} />
            ) : (
              <AiSuggestedPrompts />
            )}
            <div className="px-3 pb-3 pt-1">
              <AiComposer onSend={handleSend} size="compact" showContext autoFocus={isOpen} />
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  )
}
